import { format, parseISO } from 'date-fns';
import { getDateLocale } from '../../../utils/dateLocale';

import Modal from '../../../components/UI/Modals/Modal';
import { Diagnostic } from '../../../store/slices/checkIn';
import { useAppDispatch, useAppSelector } from '../../../store/store';
import classes from './CheckInModal.module.css';
import {
  resetCreateStatus,
  createWorkOrderServiceDiagnostic,
  selectWorkOrderServiceDiagnosticCreateStatus,
  selectWorkOrderServiceDiagnosticErrorCode,
} from '../../../store/slices/workOrderServiceDiagnostic';
import {
  IServiceInfo,
  fetchServices,
  selectServiceInfos,
  selectServicesFetchStatus,
} from '../../../store/slices/services';
import { fetchSalesOrders, fetchServicesTranslations, selectServicesTranslations } from '../../../store/slices/salesOrders';
import {
  selectAccountInfo,
  IAccountInfo,
  selectUserLocation,
  selectPersonsInLocation,
  IPerson,
  selectBusinessPartnerId,
} from '../../../store/slices/auth';
import { fetchDiagnosticResults } from '../../../store/slices/diagnosticResults';
import { useEffect, useMemo, useState } from 'react';
import Spinner from '../../../components/UI/Spinner/Spinner';

import scanIcon from '../../../assets/icons/scan.svg';
import PrimaryButtonV2 from '../../../components/UI/Buttons/PrimaryButtonV2';
import { fetchCustomers, ICustomer, selectCustomers } from '../../../store/slices/customers';
import CustomDropdown from '../../../components/UI/Inputs/CustomDropdown';
import { barCodeValidation, buildTranslatedServicesMap, getTranslatedServicesName } from '../../../utils/miscUtils';
import { client } from '../../../networking/client';
import { toast } from 'react-toastify';
import { useTranslation } from '../../../hooks/useTranslations';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  diagnostic?: Diagnostic;
  onScanClick: () => void;
  scanValue: string;
  onChangeScanValue: (e: any) => void;
  onBackButtonClick: () => void;
  onConfirmPayment: () => void;
  showAsPaid?: boolean;
};

const CheckInModal: React.FC<Props> = ({
  isOpen,
  onClose,
  diagnostic,
  onScanClick,
  scanValue,
  onChangeScanValue,
  onBackButtonClick,
  onConfirmPayment,
  showAsPaid,
}) => {
  const dispatch = useAppDispatch();
  const workOrderCreateStatus = useAppSelector<string>(
    selectWorkOrderServiceDiagnosticCreateStatus
  );
  const workOrderErrorCode = useAppSelector<number | null>(
    selectWorkOrderServiceDiagnosticErrorCode
  );
  const lang = localStorage.getItem('lang') || 'en';
  const serviceInfos = useAppSelector<IServiceInfo[]>(selectServiceInfos);
  const serviceFetchStatus = useAppSelector<string>(selectServicesFetchStatus);
  const accountInfo = useAppSelector<IAccountInfo>(selectAccountInfo);
  const personsInLocation = useAppSelector<IPerson[]>(selectPersonsInLocation);
  const customers = useAppSelector<ICustomer[]>(selectCustomers);
  const [isCreatingWorkOrder, setIsCreatingWorkOrder] = useState<boolean>(false);
  const [serviceInfoFetched, setServiceInfoFetched] = useState<boolean>(false);
  const [isOutsideLaboratory, setIsOutsideLaboratory] = useState<boolean>(false);
  const userLocation: string = useAppSelector<string>(selectUserLocation);
  const [personIdSelected, setPersonIdSelected] = useState<number>();
  const businessPartnerId = useAppSelector<string>(selectBusinessPartnerId);
  const servicesTranslations = useAppSelector(selectServicesTranslations);

  const { translations } = useTranslation();
  const isPcr = diagnostic?.diagnostic.product?.product_name.includes('PCR');
  const isAntigenAntibody =
    diagnostic?.diagnostic.product?.product_name.toLowerCase().includes('antigen') ||
    diagnostic?.diagnostic.product?.product_name.toLowerCase().includes('antibody') ||
    diagnostic?.diagnostic.product?.product_name.toLowerCase().includes('antikörpertest');

  const isDiagnosticPaid = useMemo(() => {
    if (diagnostic?.diagnostic?.sales_order?.paid !== undefined) {
      return diagnostic?.diagnostic.sales_order.paid === 'YES' || !!showAsPaid;
    }
    return false;
  }, [diagnostic, showAsPaid]);

  useEffect(() => {
    if (personsInLocation?.length > 0 && personsInLocation[0].person_id) {
      setPersonIdSelected(parseInt(personsInLocation[0].person_id));
    }
  }, [personsInLocation]);
  useEffect(() => {
    if (workOrderCreateStatus === 'fulfilled') {
      toast.success(translations('successfully.checked-in.'), {
        position: toast.POSITION.BOTTOM_RIGHT,
      });
    }
  }, [workOrderCreateStatus]);
  useEffect(() => {
    dispatch(fetchCustomers());
    dispatch(fetchServicesTranslations());
    dispatch(fetchServices(diagnostic?.diagnostic.service.service_id?.toString() ?? ''));
  }, [dispatch, diagnostic?.diagnostic.service.service_id]);

  useEffect(() => {
    if (serviceFetchStatus === 'fulfilled') {
      if (serviceInfos && serviceInfos.length > 0) {
        let serviceInfoOutsideLaboratory = serviceInfos.find(
          (serviceInfo) =>
            serviceInfo.service_execution_category_id == 4 &&
            serviceInfo.location_external_id === accountInfo.account_number
        );
        if (serviceInfoOutsideLaboratory) {
          setIsOutsideLaboratory(true);
        }
      }
      setServiceInfoFetched(true);
    } else {
      setServiceInfoFetched(false);
      setIsOutsideLaboratory(false);
    }
  }, [serviceFetchStatus, accountInfo, serviceInfos]);

  useEffect(() => {
    if (workOrderCreateStatus === 'pending') {
      setIsCreatingWorkOrder(true);
    } else if (workOrderCreateStatus === 'fulfilled') {
      dispatch(resetCreateStatus());
      // ToDo: call API if isOutside
      if (isOutsideLaboratory) {
        postOrderToExternalLaboratory();
      }
      setIsCreatingWorkOrder(false);
      dispatch(fetchSalesOrders());
      dispatch(fetchDiagnosticResults());
      onBackButtonClick();
      onClose();
    } else if (workOrderCreateStatus === 'rejected') {
      toast.error(
        translations(
          workOrderErrorCode ? `error.${workOrderErrorCode}` : 'placeholder.something.went.wrong'
        ),
        {
          position: toast.POSITION.BOTTOM_RIGHT,
        }
      );
      dispatch(resetCreateStatus());
      setIsCreatingWorkOrder(false);
    }
  }, [workOrderCreateStatus]);

  const customerInfo: ICustomer | undefined = useMemo(() => {
    return customers.find(
      (person) => person.person_id == diagnostic?.diagnostic?.person?.person_id
    );
  }, [customers, diagnostic?.diagnostic?.person?.person_id]);
  const postOrderToExternalLaboratory = async () => {
    try {
      const biomarkersResponse = await client.get(
        `/healthpoint/biomarkers_by_sample_barcode/${scanValue}`,
        {
          headers: {
            Authorization: 'Basic aGVhbHRocG9pbnQ6aGVhbHRocG9pbnQyMDIy',
          },
        }
      );
      await client.post('/healthpoint/hl7-order', {
        ta_id: diagnostic?.diagnostic.sales_order.ta_id,
        product_order_id: diagnostic?.diagnostic?.sales_order.sales_order_id,
        product_order_item_id: diagnostic?.diagnostic.product.product_id,
        sendingApplication: 'NEU',
        sendingFacility: 'MF',
        receivingApplication: 'LABOR',
        receivingFacility: 'LABOR',
        patientId: customerInfo?.person_account_number,
        firstName: customerInfo?.person_firstname,
        lastName: customerInfo?.person_lastname,
        birthDate: customerInfo?.person_birthdate.slice(0, 10).split('-').join(''),
        gender: customerInfo?.gender?.charAt(0),
        visitNumber: diagnostic?.diagnostic?.sales_order.sales_order_id.toString(),
        placerOrderNumber: scanValue,
        orderingProviderName: 'Aliyev,Farhad',
        testOrders: biomarkersResponse.data.biomarkers?.map((b: any) => {
          return { code: b.uunr, name: b.biomarker_short_abbreviation };
        }),
        orderControlCode: 'XO',
        orderStatus: 'SC',
        enteringOrganizationCode: 'FacilityCode',
      });
      toast.success(translations('sent.laboratory').toString(), {
        position: toast.POSITION.BOTTOM_RIGHT,
      });
    } catch (err) {
      console.log(err);
      toast.error(translations('not.sent.laboratory').toString(), {
        position: toast.POSITION.BOTTOM_RIGHT,
      });
    }
  };
  const translatedServicesMap = useMemo(
  () => buildTranslatedServicesMap(servicesTranslations, lang),
  [servicesTranslations, lang]
);
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      containerPadding="0"
      width="60%"
      maxWidth={'900px'}
      header={translations('check.in.customer')}
      headerStyles={{ padding: '15px' }}
    >
      <div className={classes.container}>
        <div className={classes.content}>
          <div className={classes.patientContainer}>
            <div className={classes.patientTitle}>{translations('patient.information')}</div>
            <div className={classes.patientInfoContainer}>
              <div className={classes.patientInfoLeft}>
                <div className={classes.infoTitle}>{translations('name')}</div>
                <div className={classes.infoTitle}>{translations('date_of_birth')}</div>
                <div className={classes.infoTitle}>{translations('address')}</div>
                <div className={classes.infoTitle}>{translations('id.passport')}</div>
                <div className={classes.infoTitle}>{translations('email')}</div>
                <div className={classes.infoTitle}>{translations('label.order_id')}</div>
                <div className={classes.infoTitle}>{translations('booking.date')}</div>
                <div className={classes.infoTitle}>{translations('appointment.date')}</div>
                <div className={classes.infoTitle}>{translations('test.service')}</div>
                <div className={classes.infoTitle}>{translations('location')}</div>
              </div>
              <div className={classes.patientInfoRight}>
                <div className={classes.infoContent}>
                  {`${customerInfo?.person_firstname} ${customerInfo?.person_lastname}`}
                </div>
                <div className={classes.infoContent}>
                  {customerInfo?.person_birthdate &&
                    format(parseISO(customerInfo?.person_birthdate?.toString()), 'dd.MM.yyyy', {
                      locale: getDateLocale(lang),
                    })}
                </div>
                <div className={classes.infoContent}>
                  {customerInfo?.house_number
                    ? `${customerInfo?.house_number}, ${customerInfo?.street}, ${customerInfo?.city}`
                    : ''}
                </div>
                <div className={classes.infoContent}>{customerInfo?.person_identity_number}</div>
                <div className={classes.infoContent}>{customerInfo?.person_email}</div>
                <div className={classes.infoContent}>
                  {`${diagnostic?.diagnostic.sales_order.transaction_id}/${diagnostic?.diagnostic.sales_order.ta_id}`}
                </div>
                <div className={classes.infoContent}>
                  {diagnostic
                    ? format(
                        parseISO(
                          diagnostic?.diagnostic.sales_order.datetime_create.toString() ?? ''
                        ),
                        'dd.MM.yyyy, EEEE HH:mm',
                        { locale: getDateLocale(lang) }
                      )
                    : ''}
                </div>
                <div className={classes.infoContent}>
                  {diagnostic && diagnostic?.diagnostic.sales_order_appointment?.start
                    ? format(
                        parseISO(
                          diagnostic?.diagnostic.sales_order_appointment.start.toString() ?? ''
                        ),
                        'dd.MM.yyyy, EEEE HH:mm',
                        { locale: getDateLocale(lang) }
                      )
                    : ''}
                </div>
                <div className={classes.infoContent}>
                  {/* {diagnostic?.diagnostic.product?.product_name} */}
                      {getTranslatedServicesName(
    diagnostic?.diagnostic.service?.service_id,
    translatedServicesMap
  ) }
                </div>
                <div className={classes.infoContent}>{userLocation}</div>
              </div>
            </div>
            <div className={classes.paymentSection}>
              <div className={classes.paymentSubSection}>
                <div className={classes.paymentSectionLabel}>{translations('payment.status')}</div>
                <div className={classes.paymentSectionValue}>
                  <div className={classes.price}>
                    €{diagnostic?.diagnostic?.sales_order_item?.price}
                  </div>
                  <div
                    className={
                      isDiagnosticPaid
                        ? classes.paymentGreenStatusBox
                        : classes.paymentOrangeStatusBox
                    }
                  >
                    {isDiagnosticPaid ? translations('label.paid') : translations('label.onsite')}
                  </div>
                  <div className={classes.paymentHint}>
                    {isDiagnosticPaid
                      ? `${translations('label.payment_status.hint.paid')} ${
                          diagnostic?.diagnostic.sales_order.payment_method
                        }`
                      : translations('label.payment_status.hint.to_be_paid')}
                  </div>
                </div>
              </div>
              {!isDiagnosticPaid && (
                <PrimaryButtonV2
                  text={translations('confirm.payment')}
                  containerStyle={{}}
                  buttonStyle={{
                    height: 50,
                    width: '100%',
                  }}
                  onClick={onConfirmPayment}
                  invertedColors={true}
                />
              )}
            </div>
          </div>
          <div className={classes.sampleContainer}>
            <div className={classes.patientTitle}>{translations('activate.sample.id')}</div>
            <div className={classes.sampleScanContainer}>
              <div className={classes.scanContainer}>
                <input
                  type="text"
                  className={classes.scanField}
                  placeholder={translations('placeholder.scan.or.write..').toString()}
                  value={scanValue}
                  onChange={onChangeScanValue}
                />
                <img
                  src={scanIcon}
                  alt={'scan icon'}
                  className={classes.scanButton}
                  onClick={onScanClick}
                />
              </div>
            </div>
            {isPcr && (
              <div className={classes.barcodeHint}>{translations('barcode.hint.pcr')} </div>
            )}
            {isAntigenAntibody && (
              <div className={classes.barcodeHint}>{translations('barcode.hint.antigen')} </div>
            )}
            <div className={classes.sampleScanContainer}>
              <div className={classes.scanContainer}>
                <CustomDropdown
                  label={translations('select.employee')}
                  placeholder={translations('select.employee.taking')}
                  onChange={(e: any) => setPersonIdSelected(e.target.value)}
                  options={personsInLocation.map((person) => {
                    return {
                      id: person.person_id,
                      label: `${person.person_firstname} ${person.person_lastname}`,
                    };
                  })}
                  value={personIdSelected}
                />
              </div>
            </div>
            {isCreatingWorkOrder || !serviceInfoFetched ? (
              <Spinner />
            ) : (
              <PrimaryButtonV2
                text={translations('check.in')}
                containerStyle={{
                  marginTop: 20,
                }}
                buttonStyle={{
                  height: 50,
                  width: '100%',
                }}
                disabled={
                  barCodeValidation(scanValue, personIdSelected, isPcr, isAntigenAntibody) ||
                  !isDiagnosticPaid
                }
                onClick={() => {
                  dispatch(
                    createWorkOrderServiceDiagnostic({
                      salesOrderId: diagnostic?.diagnostic.sales_order.sales_order_id ?? 0,
                      salesOrderItemId:
                        diagnostic?.diagnostic.sales_order_item.sales_order_item_id ?? 0,
                      serviceId: diagnostic?.diagnostic.service.service_id ?? 0,
                      employeeId: personIdSelected ?? 0,
                      businessPartnerId: businessPartnerId,
                      sampleBarcode: scanValue,
                      isOutsideLaboratory: isOutsideLaboratory,
                      personEmail: diagnostic?.diagnostic?.person?.person_email ?? '',
                      personAccountNumber:
                        diagnostic?.diagnostic?.person?.person_account_number ?? '',
                      taId: diagnostic?.diagnostic.sales_order.ta_id ?? 0,
                      transactionId: diagnostic?.diagnostic.sales_order.transaction_id ?? 0,
                    })
                  );
                }}
              />
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default CheckInModal;
